import { reactive } from "vue"
import store from "@/store"
import router from "@/router"
import config from "@/config/app"

// Signs the user out after a period of inactivity, with a warning beforehand.
// The last-activity time lives in localStorage so every open tab shares it.
const IDLE_LIMIT_MS = 30 * 60 * 1000
const WARNING_MS = 60 * 1000
const ACTIVITY_WRITE_INTERVAL_MS = 5 * 1000
const ACTIVITY_KEY = config.APP_NAME + "_last_activity"
const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "wheel",
  "scroll",
  "touchstart",
]

export const TIMEOUT_REASON = "inactivity"

export const idleSessionState = reactive({
  warningVisible: false,
  secondsLeft: 0,
})

let lastWrite = 0
let started = false

const isSessionActive = () => {
  const authDetails = store.getters["auth/getAuthenticationDetails"]
  return !!(
    store.getters["session/getIsSessionLoggeduser"] &&
    authDetails &&
    authDetails["access_token"]
  )
}

const readLastActivity = () => {
  try {
    const value = parseInt(localStorage.getItem(ACTIVITY_KEY), 10)
    return isNaN(value) ? null : value
  } catch (e) {
    return null
  }
}

const writeLastActivity = () => {
  lastWrite = Date.now()
  try {
    localStorage.setItem(ACTIVITY_KEY, String(lastWrite))
  } catch (e) {
    // storage unavailable: this tab falls back to an untracked session
  }
}

const getIdleMs = () => {
  const lastActivity = readLastActivity()
  if (lastActivity === null) {
    writeLastActivity()
    return 0
  }
  return Math.max(0, Date.now() - lastActivity)
}

const hideWarning = () => {
  idleSessionState.warningVisible = false
  idleSessionState.secondsLeft = 0
}

const goToSignIn = async (query = {}) => {
  hideWarning()
  await store.dispatch("globalReset")
  router.push({ path: "/signin", query })
}

export const expireSession = () => {
  const route = router.currentRoute.value
  const query = { timeout: TIMEOUT_REASON }
  if (route && route.name && route.name !== "signin") {
    query.redirect = route.name
    query.params = JSON.stringify(route.params)
    query.query = JSON.stringify(route.query)
  }
  return goToSignIn(query)
}

const checkIdle = () => {
  if (!isSessionActive()) {
    if (idleSessionState.warningVisible) hideWarning()
    return
  }
  const remainingMs = IDLE_LIMIT_MS - getIdleMs()
  if (remainingMs <= 0) {
    expireSession()
  } else if (remainingMs <= WARNING_MS) {
    idleSessionState.warningVisible = true
    idleSessionState.secondsLeft = Math.ceil(remainingMs / 1000)
  } else if (idleSessionState.warningVisible) {
    hideWarning()
  }
}

// Once the warning is up, only "Stay signed in" extends the session, so moving
// the mouse towards the dialog does not silently dismiss it.
const onUserActivity = () => {
  if (Date.now() - lastWrite < ACTIVITY_WRITE_INTERVAL_MS) return
  if (isSessionActive() && IDLE_LIMIT_MS - getIdleMs() <= WARNING_MS) return
  writeLastActivity()
}

export const staySignedIn = () => {
  writeLastActivity()
  hideWarning()
}

export const signOutNow = () => goToSignIn()

// Minutes since the user was last active, for the API's idle-time header.
export const getIdleMinutes = () => {
  if (!isSessionActive()) return -1
  return Math.floor(getIdleMs() / 60000)
}

export const startIdleSession = () => {
  if (started) return
  started = true
  ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, onUserActivity, {
      passive: true,
      capture: true,
    })
  })
  // a fresh sign-in starts a fresh idle period
  store.watch(isSessionActive, (active) => {
    if (active) writeLastActivity()
  })
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) checkIdle()
  })
  router.isReady().then(() => {
    checkIdle()
    setInterval(checkIdle, 1000)
  })
}
